import { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider } from './hooks/useAuth';
import { CompanyProvider } from './contexts/CompanyContext';
import { SubscriptionProvider } from './hooks/useSubscription';
import { PropertyProvider } from './hooks/usePropertyStore';
import { FavoritesProvider } from './hooks/useFavorites';
import { MessagesProvider } from './hooks/useMessages';
import { AnalyticsProvider } from './hooks/useAnalytics';
import { ToastProvider } from './components/common/ToastProvider';
import { MotionProvider } from './components/providers/MotionProvider';
import { SmoothScrollProvider } from './components/providers/SmoothScrollProvider';
import { AppRoutes } from './router';
import FeedbackWidget from './components/feature/FeedbackWidget';
import ReauthModal from './components/admin/ReauthModal';
import CookieConsentBanner from './components/consent/CookieConsentBanner';

function App() {
  const { i18n } = useTranslation();

  useEffect(() => {
    // Update document direction and language on initial load and language change
    const updateDocumentLanguage = () => {
      // Save scroll position before direction change
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;

      const dir = i18n.dir();
      const lang = i18n.language || i18n.resolvedLanguage || 'en';
      document.documentElement.dir = dir;
      document.documentElement.lang = lang;
      // Also set on body for better CSS targeting
      document.body.dir = dir;
      document.body.setAttribute('lang', lang);

      // Restore scroll position after layout settles
      requestAnimationFrame(() => {
        window.scrollTo(scrollX, scrollY);
      });
    };

    // Update on mount
    updateDocumentLanguage();

    // Listen for language changes
    i18n.on('languageChanged', updateDocumentLanguage);

    return () => {
      i18n.off('languageChanged', updateDocumentLanguage);
    };
  }, [i18n]);

  return (
    <MotionProvider>
      <AuthProvider>
        <ToastProvider>
          <CompanyProvider>
            <SubscriptionProvider>
              <AnalyticsProvider>
                <MessagesProvider>
                  <FavoritesProvider>
                    <PropertyProvider>
                      <BrowserRouter basename={__BASE_PATH__}>
                        <SmoothScrollProvider>
                          <AppRoutes />
                          <FeedbackWidget />
                          <ReauthModal />
                          <CookieConsentBanner />
                        </SmoothScrollProvider>
                      </BrowserRouter>
                    </PropertyProvider>
                  </FavoritesProvider>
                </MessagesProvider>
              </AnalyticsProvider>
            </SubscriptionProvider>
          </CompanyProvider>
        </ToastProvider>
      </AuthProvider>
    </MotionProvider>
  );
}

export default App;
